// =============================================================================
// Import limits config — the shape of import-config/limits.json in the
// data-importer's S3 bucket.
//
// TWO KINDS OF LIMIT LIVE IN THIS FILE AND THEY ARE DIFFERENT UNITS. The
// separation is deliberate and is mirrored here in the types so the UI cannot
// blur it either:
//
//   importLimits     ROWS IN ONE FILE, for one entity, per import. Resets every
//                    import. 5000 = "this spreadsheet may have 5000 lines".
//                    ENFORCED server-side: both commit routes in the Python
//                    importer check it and reject an over-limit file with 422
//                    before any work happens.
//
//   selectionLimits  HOW MANY ITEMS the tenant may have, cumulative and
//                    permanent. 3 = "this account may have three".
//                    NOT ENFORCED server-side. Currency, ports and languages
//                    are written straight to AppSync from the browser and never
//                    pass through the importer, so there is nothing to
//                    intercept. These numbers are a guard rail in the UI, not a
//                    control. The screen says so; do not quietly imply
//                    otherwise.
//
// Key names differ between the blocks on purpose — selection keys are
// currency_extras / ports / languages, never wizard_currency — so that
// "currency: 3" can never be read as three rows per import.
// =============================================================================

/** The two tiers. Operator-declared; nothing infers it. */
export type Tier = 'trial' | 'purchased';

/** Applies to every key in a block that has no entry of its own. */
export const DEFAULT_KEY = '_default';

/** The three selection caps, matching SELECTION_FALLBACKS in the importer. */
export type SelectionKind = 'currency_extras' | 'ports' | 'languages';

export const SELECTION_KINDS: {
  kind: SelectionKind; label: string; hint: string; replaces: string;
}[] = [
  { kind: 'currency_extras', label: 'Currencies',
    hint: 'beyond their base currency', replaces: '3' },
  { kind: 'ports',           label: 'Shipping ports',
    hint: 'chosen from the port catalogue', replaces: '20' },
  { kind: 'languages',       label: 'Languages',
    hint: 'the account is translated into', replaces: '3' },
];

/** entity/kind -> limit. `_default` is allowed alongside the specific keys. */
export type LimitMap = Record<string, number | string | string[] | undefined>;

export interface TenantBlock extends LimitMap {
  /** ONLY meaningful in importLimits.tenants — the tier lives in one place and
   *  both units read it. Absent means UNKNOWN, which resolves to `purchased`. */
  tier?: Tier;
}

export interface LimitsConfig {
  schema_version?: number;
  _readme?: string[];
  import_limits?: {
    tiers?: Partial<Record<Tier, LimitMap>>;
    tenants?: Record<string, TenantBlock>;
  };
  selection_limits?: {
    _readme?: string[];
    tiers?: Partial<Record<Tier, LimitMap>>;
    tenants?: Record<string, LimitMap>;
  };
  /** v1 kept row limits at the root. The importer still reads that shape, so
   *  the editor has to be able to open one and migrate it on save. */
  tiers?: Partial<Record<Tier, LimitMap>>;
  tenants?: Record<string, TenantBlock>;
}

/** What the API returns: the document plus the ETag needed to write it back. */
export interface LimitsDocument {
  config: LimitsConfig;
  /** S3 object ETag. Sent back as If-Match so a blind overwrite is impossible. */
  etag: string;
  /** Server-side last-modified, for the "changed by someone else" message. */
  lastModified?: string | null;
}

/** True when the document is still in the v1 root-level shape. */
export function isLegacyShape(cfg: LimitsConfig): boolean {
  return !cfg.import_limits && !!(cfg.tiers || cfg.tenants);
}

/**
 * True when the document holds both layouts at once — an `import_limits` block
 * AND row limits still at the root.
 *
 * The importer resolves `import_limits or root`, so once `import_limits`
 * exists the root block is not read at all. A file in this state is running on
 * built-in fallbacks for everything the root block used to cover.
 */
export function hasSplitRowLimits(cfg: LimitsConfig): boolean {
  return !!cfg.import_limits && !!(cfg.tiers || cfg.tenants);
}

/**
 * Normalise a document to the v2 shape for editing. Idempotent, non-mutating.
 *
 * The editor reads row limits through rowLimitsOf(), which falls back to the
 * root when `import_limits` is absent, but writes them to `import_limits`. On a
 * v1 file those disagree after one keystroke: creating `import_limits` moves
 * the reader off the root, blanking every other field. Migrating once on load
 * keeps a single shape in play so the two cannot diverge.
 *
 * Where both layouts exist, `import_limits` wins key by key and the root fills
 * in the rest — which repairs a file already split this way.
 */
export function toEditableShape(cfg: LimitsConfig): LimitsConfig {
  const next: LimitsConfig = JSON.parse(JSON.stringify(cfg ?? {}));

  const rootTiers = next.tiers;
  const rootTenants = next.tenants;
  delete next.tiers;
  delete next.tenants;
  if (!rootTiers && !rootTenants) { return next; }

  const block = next.import_limits ?? (next.import_limits = {});

  if (rootTiers) {
    const tiers = block.tiers ?? (block.tiers = {});
    (Object.keys(rootTiers) as Tier[]).forEach(t => {
      tiers[t] = { ...(rootTiers[t] || {}), ...(tiers[t] || {}) };
    });
  }
  if (rootTenants) {
    const tenants = block.tenants ?? (block.tenants = {});
    Object.keys(rootTenants).forEach(g => {
      tenants[g] = { ...(rootTenants[g] || {}), ...(tenants[g] || {}) };
    });
  }
  return next;
}

/** Read row limits from either shape without the caller caring which. */
export function rowLimitsOf(cfg: LimitsConfig) {
  const root = cfg.import_limits ?? { tiers: cfg.tiers, tenants: cfg.tenants };
  return { tiers: root.tiers ?? {}, tenants: root.tenants ?? {} };
}

export function selectionLimitsOf(cfg: LimitsConfig) {
  const root = cfg.selection_limits ?? {};
  return { tiers: root.tiers ?? {}, tenants: root.tenants ?? {} };
}

/**
 * How the tier the importer will apply was arrived at.
 *
 * Never show a bare word. "purchased because they paid", "purchased because an
 * operator pinned it" and "purchased because the lookup failed" are three
 * different facts, and an operator deciding whether to pin an override needs to
 * see which one they are looking at.
 */
export interface TierStatus {
  /** What the importer will actually use. */
  tier: Tier;
  /** An operator has pinned this in config, overriding detection. */
  pinned: boolean;
  /** What isPurchase on the tenant record said, regardless of the pin. */
  detectedTier: Tier | null;
  /** False when the lookup failed and `detectedTier` is not a fact. */
  detected: boolean;
}

/**
 * Resolve the tier the way the importer does:
 *
 *   1. explicit "tier" pinned in config   operator override, rare
 *   2. isPurchase on the tenant record    the real signal
 *   3. purchased                          lookup failed — never throttle a
 *                                         paying customer over a blip
 *
 * `detected` comes from /import/meta's `tier_detail`, which is the server's own
 * answer. The UI does not re-derive it — it would be guessing at a value only
 * the server can read.
 */
export function tierStatus(
  cfg: LimitsConfig,
  tenantGuid: string,
  detected?: { tier?: string; detected?: boolean } | null,
): TierStatus {
  const { tenants } = rowLimitsOf(cfg);
  const pin = tenants?.[tenantGuid]?.tier;
  const detTier = (detected?.tier === 'trial' || detected?.tier === 'purchased')
    ? (detected.tier as Tier) : null;
  const wasDetected = !!detected?.detected && !!detTier;

  if (pin === 'trial' || pin === 'purchased') {
    return { tier: pin, pinned: true, detectedTier: detTier, detected: wasDetected };
  }
  return {
    tier: wasDetected ? (detTier as Tier) : 'purchased',
    pinned: false,
    detectedTier: detTier,
    detected: wasDetected,
  };
}

/** The sentence the admin screen shows. Explicit by construction — there is no
 *  branch here that produces a bare tier name. */
export function describeTier(s: TierStatus): string {
  if (s.pinned) {
    if (s.detected && s.detectedTier && s.detectedTier !== s.tier) {
      return `${s.tier} (pinned, detected ${s.detectedTier})`;
    }
    if (s.detected && s.detectedTier === s.tier) {
      return `${s.tier} (pinned, matches detected)`;
    }
    return `${s.tier} (pinned, detection unavailable)`;
  }
  if (s.detected) { return `${s.tier} (detected)`; }
  return `${s.tier} (defaulting — the tenant record could not be read)`;
}

/** A limit must be a positive whole number; anything else is ignored by the
 *  importer and falls through to the next level. The form enforces the same
 *  rule so what you see saved is what will resolve. */
export function isValidLimit(v: unknown): boolean {
  if (v === '' || v === null || v === undefined) return true;   // means "unset"
  const n = Number(v);
  return Number.isFinite(n) && Number.isInteger(n) && n > 0;
}
